import { useSelectRandomQuote } from '@/hooks/use-random-quotes';
import { Heading } from '@rhg/components/heading';
import { Stack } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import dynamic from 'next/dynamic';
import { useRef, useEffect } from 'react';
import { LoadingDialogContentProps } from '../types';

const LoadingDots = dynamic(() =>
    import('@rhg/components/animations/loading-dots').then(
        (mod) => mod.LoadingDots
    )
);

const getContentHeadingStyles = (shouldShowQuote?: boolean) => ({
    ...(shouldShowQuote
        ? {
              justifyContent: 'center',
              height: '80dvh',
          }
        : {
              mt: 4,
              pt: 2,
          }),
});

const styleDialogContent = {
    height: '100%',
};

const styleNewLines = (shouldShowQuote?: boolean) => ({
    whiteSpace: 'pre-line',
    ...(shouldShowQuote ? { color: 'text.body' } : {}),
});

const styleHeadingPlacement = (shouldShowQuote?: boolean) => {
    return shouldShowQuote
        ? {
              justifyContent: 'center',
              height: '80dvh',
          }
        : {};
};

const styleQuoteTitle = {
    fontStyle: 'italic',
    color: 'text.secondary',
};

export const LoadingDialogQuoteContent = ({
    titleId,
    title,
    subtitle,
    titleVariant = 'h4',
    subtitleVariant = 'h5',
    boxStyles,
    headingStyles,
    isReady = true,
    isConciergeLoadingDialog = false,
    shouldShowQuote = false,
}: LoadingDialogContentProps) => {
    const loadingQuote = useSelectRandomQuote();
    const quoteTitle = `"${loadingQuote?.text}"`;
    const isQuoteShowing = shouldShowQuote && loadingQuote;
    const hasQuoteEverShown = useRef(false);

    // Track if quote has ever been shown
    useEffect(() => {
        if (isQuoteShowing) {
            hasQuoteEverShown.current = true;
        }
    }, [isQuoteShowing]);

    // Lazily load loading dots since lottie is huge
    return (
        <Stack
            direction="column"
            alignItems="center"
            sx={sxAppend(styleDialogContent, boxStyles)}
        >
            {isReady && <LoadingDots loop />}
            {isQuoteShowing || hasQuoteEverShown.current ? (
                <Stack sx={styleHeadingPlacement(isConciergeLoadingDialog)}>
                    <Heading
                        title={quoteTitle}
                        titleAlign="center"
                        titleVariant={titleVariant}
                        titleComponent="h2"
                        titleId={titleId}
                        titleStyle={styleQuoteTitle}
                        subtitle={
                            loadingQuote?.attribution
                                ? `-${loadingQuote?.attribution}`
                                : undefined
                        }
                        subtitleAlign="center"
                        subtitleVariant={subtitleVariant}
                        subtitleStyle={styleNewLines(isConciergeLoadingDialog)}
                        sx={sxAppend(
                            getContentHeadingStyles(isConciergeLoadingDialog),
                            headingStyles
                        )}
                    />
                </Stack>
            ) : (
                !hasQuoteEverShown.current && (
                    <Heading
                        title={title}
                        titleAlign="center"
                        titleVariant={titleVariant}
                        titleComponent="h2"
                        titleId={titleId}
                        subtitle={subtitle}
                        subtitleAlign="center"
                        subtitleVariant="body1"
                        subtitleStyle={styleNewLines(isConciergeLoadingDialog)}
                        sx={sxAppend(
                            getContentHeadingStyles(isConciergeLoadingDialog),
                            headingStyles
                        )}
                    />
                )
            )}
        </Stack>
    );
};
